import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import find from 'ramda/src/find';
import isNil from 'ramda/src/isNil';
import propEq from 'ramda/src/propEq';
import toLower from 'ramda/src/toLower';
import cx from 'classnames';
import moment from 'moment';
import Avatar from '@components/@core/Avatar';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import EditDropdown from '@components/EditDropdown';
import AppActions from '~/actions/app';
import CommunityActions from '~/actions/community';
import { inPage } from '~/services/util';
import { UserRoles } from '~/services/config';

const Detail = ({ businessId, data, employees, isUser }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector((state) => state.app.user);
    const [current, setCurrent] = useState(1);
    const [per, setPer] = useState(10);

    const handlePer = (e) => {
        if (e > 50) setCurrent(1);
        setPer(e);
    };

    const handleEdit = (item) => (e) => {
        if (e === 'view profile') {
            const name = toLower(item.profile.first_name + '-' + item.profile.last_name);
            dispatch(CommunityActions.getcommunityuserRequest({ userId: item.community_user_id }, null, `/${item.community_user.entity.slug}/about?user_id=${item.community_user.entity.user_id}`));
        }
        if (e === 'edit') {
            const payload = { type: 'Edit User Content', data: { before: { user: item, employees, businessId } } };
            dispatch(AppActions.modalRequest(payload));
        }
    };

    const handleToggle = (slug) => {
        if (slug) navigate(`/${slug}/about`);
    };

    return (
        <div className="human-capital">
            {employees?.map((item, index) => {
                const department = find(propEq('id', item.department_id), data.departments);
                const role = find(propEq('id', item.job_role_id), data.job_roles);
                if (inPage(index, current, per)) {
                    return (
                        <div className={cx('card mt-3 pt-2 pt-sm-4', data.length === index + 1 && 'mb-0')} key={`e${index}`}>
                            {index === 0 && (
                                <div className="d-flex align-items-center mn-sm-2">
                                    <span className="dsl-b14 text-400 ms-auto">Showing</span>
                                    <EditDropdown className="showing" caret="down" defaultIndexes={[1]} options={['All', 'Active', 'Recruits']} />
                                </div>
                            )}
                            <div className="d-flex flex-sm-row flex-column">
                                <div className="mr-sm-5">
                                    <Avatar size="regular" url={item.avatar} name={item.name} slug={item?.slug} className="m-auto" onToggle={() => handleToggle(item?.slug)} />
                                    <p className="dsl-m11 text-center my-2">Since</p>
                                    <p className="dsl-b14 text-center mb-0">{moment(item.hired_at).format('MMM DD, YYYY')}</p>
                                </div>
                                <div className="d-flex-1">
                                    <div className="d-flex justify-content-between mt-2 mt-md-0">
                                        <span className="dsl-b16 bold ps-3 ps-md-0 m-auto m-md-0">{`${item.profile.first_name} ${item.profile.last_name}`}</span>
                                        {[UserRoles.ADMIN, UserRoles.SUPER_ADMIN].includes(user?.primary_role_id) && <EditDropdown options={['Edit', 'View Profile']} onChange={handleEdit(item)} />}
                                    </div>
                                    <div className="d-flex flex-wrap flex-lg-nowrap">
                                        <div className="d-flex-4">
                                            <div className="d-flex align-items-center mt-3">
                                                <span className="dsl-m12 width-100">Company</span>
                                                <span className="dsl-b16 d-flex-2">{data.name}</span>
                                            </div>
                                            <div className="d-flex align-items-center mt-2">
                                                <span className="dsl-m12 width-100">Job role</span>
                                                <span className="dsl-b16 d-flex-2">{role?.name}</span>
                                            </div>
                                            <div className="d-flex align-items-center mt-2">
                                                <span className="dsl-m12 width-100">Department</span>
                                                <span className="dsl-b16 d-flex-2">{department?.name}</span>
                                            </div>
                                        </div>
                                        <div className="d-flex-4 ms-lg-3 order-md-3 order-lg-2 human-capital-flex">
                                            <div className="d-flex align-items-center mt-3">
                                                <span className="dsl-m12 width-100">Phone</span>
                                                <span className="dsl-b16">{item.profile.phone}</span>
                                            </div>
                                            <div className="d-flex align-items-center mt-2">
                                                <span className="dsl-m12 width-100">Cell</span>
                                                <span className="dsl-b16">{item.profile.phone}</span>
                                            </div>
                                            <div className="d-flex align-items-center mt-2">
                                                <span className="dsl-m12 width-100">Email</span>
                                                <span className={`dsl-b16 ${!isNil(item?.extra?.training) && 'maxw-170'}`}>{item.email}</span>
                                            </div>
                                        </div>
                                        {isUser && !isNil(item?.extra?.training) && (
                                            <div className="d-flex-2 mt-3 mt-md-0 ms-md-3 order-md-2 order-lg-3 human-capital-mobile-flex">
                                                <div className="d-flex align-items-center">
                                                    {item.extra.training > 0 ? (
                                                        <Icon name="fas fa-circle-check" color="green" size={16} />
                                                    ) : (
                                                        <Icon name="fas fa-circle-exclamation" color="red" size={16} />
                                                    )}
                                                    <span className="dsl-b16 ms-2">Learning</span>
                                                </div>
                                                <div className="d-flex align-items-center mt-2">
                                                    {item.extra.scorecards.length > 0 ? (
                                                        <Icon name="fas fa-circle-check" color="green" size={16} />
                                                    ) : (
                                                        <Icon name="fas fa-circle-exclamation" color="red" size={16} />
                                                    )}
                                                    <span className="dsl-b16 ms-2">Scorecard</span>
                                                </div>
                                                <div className="d-flex align-items-center mt-2">
                                                    {item.extra.programs.length > 0 ? (
                                                        <Icon name="fas fa-circle-check" color="green" size={16} />
                                                    ) : (
                                                        <Icon name="fas fa-circle-exclamation" color="red" size={16} />
                                                    )}
                                                    <span className="dsl-b16 ms-2">Health Plan</span>
                                                </div>
                                                <div className="d-flex align-items-center mt-2">
                                                    {item.extra.habits > 0 ? (
                                                        <Icon name="fas fa-circle-check" color="green" size={16} />
                                                    ) : (
                                                        <Icon name="fas fa-circle-exclamation" color="red" size={16} />
                                                    )}
                                                    <span className="dsl-b16 ms-2">Habits</span>
                                                </div>
                                                <div className="d-flex align-items-center mt-2">
                                                    {item.extra.certifications.length > 0 ? (
                                                        <Icon name="fas fa-circle-check" color="green" size={16} />
                                                    ) : (
                                                        <Icon name="fas fa-circle-exclamation" color="red" size={16} />
                                                    )}
                                                    <span className="dsl-b16 ms-2">Certifications</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                }
            })}
            <Pagination current={current} total={Math.ceil(employees?.length / per)} per={per} pers={[10, 25, 50, 'all']} onChange={(e) => setCurrent(e)} onPer={handlePer} />
        </div>
    );
};

Detail.propTypes = {
    data: PropTypes.any,
};

Detail.defaultProps = {
    data: [{ name: 'Bart Wilson' }, { name: 'Brandon Groff' }],
};

export default Detail;
